import { buildRissRecords, formatRissTimestamp, formatRissCalendarDate } from './rissRecords.mjs'
import { validateRissJpegBase64 } from './rissJpeg.mjs'

export const RISS_XML_MAX_ROWS = 1000
export const RISS_XML_MAX_BYTES = 8 * 1024 * 1024
// Application safety limit, not a claimed RISS service limit. Embedded customer
// photos repeat per item, so a small input can otherwise expand to gigabytes.
export const RISS_XML_MAX_TOTAL_BYTES = 64 * 1024 * 1024
export const RISS_XML_UPLOADER_VERSION = 'GoldBuy0.1.0'
const encoder = new TextEncoder()
const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i
const isUuid = value => typeof value === 'string' && UUID.test(value) && /[1-9a-f]/i.test(value.replaceAll('-', ''))
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)
const present = value => value !== null && value !== undefined && value !== ''
const string = value => typeof value === 'string' || typeof value === 'number' ? String(value) : ''
const IMAGE_FIELDS = ['Item Filename', 'Customer ID Filename', 'Customer Filename']
const XML_ID_TYPES = { 'Drivers License': 'Driver ID' }
const RECORD_FIELDS = [
  'CustomerFirstName', 'CustomerMiddleName', 'CustomerLastName', 'CustomerSuffix',
  'CustomerDOB', 'CustomerSex', 'CustomerRace', 'CustomerEyeColor', 'CustomerHairColor',
  'CustomerHeight', 'CustomerWeight', 'CustomerAddress', 'CustomerCity', 'CustomerState',
  'CustomerZip', 'CustomerPhone', 'CustomerIdNumber', 'CustomerIdType', 'CustomerIdState', 'CustomerIdExpire',
  'DelVehManufacturer', 'DelVehModel', 'DelVehYear', 'DelVehState', 'DelVehRegNum', 'DelVehVinNum', 'DelVehColor',
  'TransactionTicketNumber', 'TransactionDate', 'TransactionTime', 'TransactionType', 'TransactionAmount',
  'ItemSerialNumber', 'ItemArticleType', 'ItemManufacturer', 'ItemModel', 'ItemInscription', 'ItemImeNumber',
  'ItemDescription', 'ItemMaterial', 'ScrapWeight', 'ScrapUnits', 'ScrapVehDisposition', 'ScrapVehYear',
  'ScrapVehRegNum', 'ScrapVehVinNum', 'ScrapVehTitleState', 'ScrapVehTitleNum', 'ScrapVehTitleDate',
  'ScrapVehColor', 'ScrapVehMileage', 'ScrapyardLicense', 'ImageItemFileName', 'ImageIdFileName',
  'ImageCustomerFileName', 'ItemQuantity', 'DateItemAcquired', 'TimeItemAcquired', 'StateAssignedVIN',
  'StockNumber', 'CustomerBusinessName',
]

function validXmlText(value) {
  if (typeof value !== 'string') return false
  for (const char of value) {
    const point = char.codePointAt(0)
    if ((point < 0x20 && ![9, 10, 13].includes(point)) ||
        (point >= 0xd800 && point <= 0xdfff) || point === 0xfffe || point === 0xffff) return false
  }
  return true
}

export function escapeRissXml(value) {
  const text = String(value)
  if (!validXmlText(text)) throw new TypeError('Text contains characters forbidden in XML 1.0.')
  return text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;').replaceAll("'", '&apos;').replaceAll('\r', '&#13;')
}

const element = (name, value, indent = '      ') => value === '' || value === undefined || value === null
  ? `${indent}<${name} />\r\n`
  : `${indent}<${name}>${escapeRissXml(value)}</${name}>\r\n`

// RISS's sample has a numeric UploadId; the UUID filename remains the strong
// batch identity. This deterministic 8-digit adapter value is not a secret.
export function stableRissUploadId(batchId, batchIndex = 0) {
  let hash = 2166136261
  for (const char of `${batchId}:${batchIndex}`) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0
  return String(10000000 + hash % 90000000)
}

function envelope(shopXml, uploadDate, batchId, batchIndex, transactionCount) {
  return '<?xml version="1.0" encoding="UTF-8"?>\r\n' +
    '<Batch xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema">\r\n' +
    '  <UploadInfo>\r\n' + element('StartDate', uploadDate, '    ') + shopXml +
    element('UploadId', stableRissUploadId(batchId, batchIndex), '    ') +
    element('UploaderVersion', RISS_XML_UPLOADER_VERSION, '    ') +
    element('TransactionsReviewed', transactionCount, '    ') + element('TransactionsExcluded', 0, '    ') +
    '  </UploadInfo>\r\n  <TransactionRecords>\r\n'
}
const FOOTER = '  </TransactionRecords>\r\n</Batch>\r\n'
const RECORD_START = '    <Transaction>\r\n'
const RECORD_END = '    </Transaction>\r\n'

// Count escaped UTF-8 without allocating the expanded text. In particular,
// never serialize a large repeated image before checking the aggregate cap.
function escapedByteLength(value) {
  let bytes = 0
  for (const char of value) {
    if (char === '&' || char === '\r') bytes += 5
    else if (char === '<' || char === '>') bytes += 4
    else if (char === '"' || char === "'") bytes += 6
    else {
      const point = char.codePointAt(0)
      bytes += point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4
    }
  }
  return bytes
}
const elementByteLength = (name, value, textSize = escapedByteLength) => value === '' || value === undefined || value === null
  ? name.length + 12 : 2 * name.length + 13 + textSize(value)

/**
 * Pure XML adapter. Never reads configuration, Firebase, files, network, or time.
 * TransactionAmount is the reconciled paid value of THIS ITEM (not repeated total).
 * images contains plain base64 JPEG data, exactly 640x480; there are no URLs.
 * configGuid is optional for local downloads and must be injected by the trusted
 * server for configured submission. No credential is embedded in this module.
 */
export function buildRissXmlExport(transactions, options = {}) {
  if (!isObject(options)) options = {}
  const attachmentFilenames = Object.create(null)
  const images = isObject(options.images) ? options.images : {}
  for (const [transactionId, media] of Object.entries(images)) {
    if (!isObject(media)) continue
    const mapping = { item: Object.create(null) }
    if (isObject(media.item)) for (const [index, value] of Object.entries(media.item)) {
      if (present(value)) mapping.item[index] = 'embedded-jpeg'
    }
    if (present(media.customerId)) mapping.customerId = 'embedded-jpeg'
    if (present(media.customer)) mapping.customer = 'embedded-jpeg'
    attachmentFilenames[transactionId] = mapping
  }
  const result = buildRissRecords(transactions, {
    startDate: options.startDate, endDate: options.endDate, timeZone: options.timeZone ?? 'America/New_York',
    weightUnit: 'grams', requiredFields: options.requiredFields, attachmentFilenames,
  }, { profile: 'xml' })
  const addError = (code, message, context = {}) => result.errors.push({ code, message, ...context })
  result.warnings = result.warnings.map(warning => warning.code === 'IMAGE_OMITTED'
    ? { ...warning, message: `${warning.field} is omitted from XML. Include a validated 640 × 480 JPEG if this image is required; private storage URLs are never exported.` }
    : warning)
  const shop = isObject(options.shop) ? options.shop : {}
  const shopFields = [
    ['name', 'ShopName'], ['street', 'ShopStreet'], ['city', 'ShopCity'], ['state', 'ShopState'],
    ['zip', 'ShopZipCode'], ['phone', 'ShopPhone'], ['email', 'ShopEmail'], ['contactName', 'ShopContactName'],
  ]
  const shopValues = {}
  for (const [key, tag] of shopFields) {
    const value = string(shop[key])
    if (['name', 'street', 'city', 'state', 'zip'].includes(key) && !value.trim()) {
      addError('MISSING_SHOP_FIELD', `Required shop information is missing: ${tag}.`, { field: tag })
    } else if (present(shop[key]) && (!value || !validXmlText(value))) {
      addError('INVALID_SHOP_FIELD', `Shop information is invalid for XML: ${tag}.`, { field: tag })
    }
    shopValues[tag] = value
  }
  if (present(shop.configGuid) && !isUuid(shop.configGuid)) addError('INVALID_SHOP_CONFIG_GUID', 'The supplied shop configuration identifier must be a UUID.', { field: 'ShopConfigGUID' })
  if (!isUuid(options.batchId)) addError('INVALID_BATCH_ID', 'Provide a unique UUID batch identity before generating XML.')
  const uploadedAt = options.uploadedAt instanceof Date && Number.isFinite(options.uploadedAt.getTime())
    ? formatRissTimestamp(options.uploadedAt, options.timeZone ?? 'America/New_York') : null
  if (!uploadedAt) addError('INVALID_UPLOAD_DATE', 'Provide an explicit valid upload date/time; the export will not invent the current time.')
  if (options.images !== undefined && !isObject(options.images)) addError('INVALID_IMAGES', 'Images must be a map keyed by purchase document ID.')
  if (result.errors.length) return result

  // configGuid is deliberately absent unless provided by the trusted caller.
  const estimatedShopBytes = Object.entries(shopValues).reduce((sum, [tag, value]) => sum + elementByteLength(tag, value), 0)
  if (estimatedShopBytes >= RISS_XML_MAX_BYTES) { addError('XML_ENVELOPE_TOO_LARGE', 'Shop/upload metadata exceeds the 8 MiB XML limit.'); return result }
  const shopXml = '    <ShopInfo>\r\n' + Object.entries(shopValues).map(([tag, value]) => element(tag, value)).join('') +
    (present(shop.configGuid) ? element('ShopConfigGUID', shop.configGuid) : '') + '    </ShopInfo>\r\n'
  const reservedEnvelopeBytes = encoder.encode(envelope(shopXml, uploadedAt.date, options.batchId, 0, RISS_XML_MAX_ROWS) + FOOTER).length
  const room = RISS_XML_MAX_BYTES - reservedEnvelopeBytes
  if (room <= 0) { addError('XML_ENVELOPE_TOO_LARGE', 'Shop/upload metadata exceeds the 8 MiB XML limit.'); return result }

  const validatedImages = new Map()
  const textSizes = new Map()
  const textSize = value => {
    if (validatedImages.get(value)?.valid) return value.length // canonical base64 is ASCII, with no XML escapes
    if (!textSizes.has(value)) textSizes.set(value, escapedByteLength(value))
    return textSizes.get(value)
  }
  const xmlRecords = []
  let estimatedTotalBytes = 0, activeBatchBytes = 0, activeBatchRows = 0
  for (const record of result.records) {
    const { fields, transactionId, itemIndex, customer } = record
    const context = { transactionId, itemIndex }
    const media = Object.hasOwn(images, transactionId) ? images[transactionId] : undefined
    if (media !== undefined && !isObject(media)) addError('INVALID_IMAGES', 'Purchase images must be a named map, not an array or scalar.', context)
    if (media?.item !== undefined && !isObject(media.item)) addError('INVALID_IMAGES', 'Item images must be mapped by the saved item index.', context)
    let expiration = ''
    if (customer.idExpiration) {
      expiration = formatRissCalendarDate(customer.idExpiration)
      if (!expiration) addError('INVALID_ID_EXPIRATION', 'A supplied customer ID expiration must be a real YYYY-MM-DD calendar date.', { ...context, field: 'CustomerIdExpire' })
    }
    const values = {
      CustomerFirstName: fields['Customer First Name'], CustomerLastName: fields['Customer Last Name'],
      CustomerDOB: fields['Customer DOB'], CustomerAddress: fields['Customer Street'], CustomerCity: fields['Customer City'],
      CustomerState: fields['Customer State'], CustomerZip: fields['Customer Zip'], CustomerPhone: customer.phone,
      CustomerIdNumber: fields['Customer ID Number'], CustomerIdType: XML_ID_TYPES[fields['Customer ID Type']] || fields['Customer ID Type'],
      CustomerIdState: customer.idState, CustomerIdExpire: expiration,
      TransactionTicketNumber: fields['Invoice #'], TransactionDate: fields.Date, TransactionTime: fields.Time,
      TransactionType: 'Buy', TransactionAmount: fields['$ Value'],
      ItemSerialNumber: fields['Serial #'], ItemArticleType: fields['Item Type'], ItemManufacturer: fields.Manufacturer,
      ItemModel: fields.Model, ItemInscription: fields.Inscription, ItemDescription: fields.Description,
      ItemMaterial: fields.Material, ScrapWeight: fields.Weight, ScrapUnits: 'grams',
      ImageItemFileName: '', ImageIdFileName: '', ImageCustomerFileName: '',
    }
    for (const [tag, value] of Object.entries(values)) {
      if (!validXmlText(value || '')) addError('INVALID_XML_CHARACTER', `The supplied ${tag} contains a character forbidden in XML 1.0.`, { ...context, field: tag })
    }
    for (const [field, tag, value] of [
      [IMAGE_FIELDS[0], 'ImageItemFileName', media?.item?.[itemIndex]],
      [IMAGE_FIELDS[1], 'ImageIdFileName', media?.customerId],
      [IMAGE_FIELDS[2], 'ImageCustomerFileName', media?.customer],
    ]) {
      if (!present(value)) continue
      let checked = validatedImages.get(value)
      if (!checked) { checked = validateRissJpegBase64(value); validatedImages.set(value, checked) }
      if (!checked.valid) for (const error of checked.errors) addError(error.code, error.message, { ...context, field })
      else values[tag] = value
    }
    if (Object.values(values).every(value => validXmlText(value || ''))) {
      // Unknown optional legacy values are absent, not zero or an empty numeric
      // element. Use the identical field list for size accounting and output.
      const recordFields = RECORD_FIELDS.filter(tag =>
        !(tag === 'TransactionAmount' && values.TransactionAmount === '') &&
        !(tag === 'CustomerIdType' && record.omitXmlIdType))
      const byteLength = RECORD_START.length + RECORD_END.length + recordFields.reduce((sum, tag) => sum + elementByteLength(tag, values[tag], textSize), 0)
      if (byteLength > room) {
        addError('XML_ROW_TOO_LARGE', 'A single item record and its embedded photos exceed the 8 MiB XML limit. Reduce image size before exporting; nothing was truncated.', context)
        return result
      }
      const newBatch = activeBatchRows === 0 || activeBatchRows === RISS_XML_MAX_ROWS || activeBatchBytes + byteLength > room
      const increase = byteLength + (newBatch ? reservedEnvelopeBytes : 0)
      if (estimatedTotalBytes + increase > RISS_XML_MAX_TOTAL_BYTES) {
        addError('XML_EXPORT_TOO_LARGE', 'The generated XML would exceed the 64 MiB application safety limit because embedded photos repeat per item. Reduce the selection or image size; no files were generated or truncated.', context)
        return result
      }
      if (newBatch) { activeBatchRows = 0; activeBatchBytes = 0 }
      activeBatchRows++; activeBatchBytes += byteLength; estimatedTotalBytes += increase
      const xml = RECORD_START + recordFields.map(tag => element(tag, values[tag])).join('') + RECORD_END
      xmlRecords.push({ xml, byteLength, transactionId, itemIndex })
    }
  }
  if (result.errors.length) return result
  const chunks = []
  let chunk = [], chunkBytes = 0
  for (const record of xmlRecords) {
    if (record.byteLength > room) {
      addError('XML_ROW_TOO_LARGE', 'A single item record and its embedded photos exceed the 8 MiB XML limit. Reduce image size before exporting; nothing was truncated.', { transactionId: record.transactionId, itemIndex: record.itemIndex })
      continue
    }
    if (chunk.length && (chunk.length === RISS_XML_MAX_ROWS || chunkBytes + record.byteLength > room)) {
      chunks.push(chunk); chunk = []; chunkBytes = 0
    }
    chunk.push(record); chunkBytes += record.byteLength
  }
  if (chunk.length) chunks.push(chunk)
  if (result.errors.length) return result
  result.batches = chunks.map((records, index) => {
    const transactionIds = [...new Set(records.map(record => record.transactionId))]
    const xml = envelope(shopXml, uploadedAt.date, options.batchId, index, transactionIds.length) + records.map(record => record.xml).join('') + FOOTER
    return { filename: `RISSProp_${options.batchId.toLowerCase()}_${String(index + 1).padStart(4, '0')}.xml`,
      xml, rowCount: records.length, transactionIds, byteLength: encoder.encode(xml).length }
  })
  if (result.batches.length > 1) result.warnings.push({ code: 'SEPARATE_XML_BATCHES', message: 'XML is split into separate files of at most 1,000 item records and 8 MiB each. Process each file separately; no records were truncated.' })
  result.warnings.push({ code: 'XML_REQUIRES_RECEIVING_VALIDATION', message: 'XML generation is not RISS submission or acceptance. TransactionAmount is exported per item when recorded; validate the receiving shop configuration and any additional required fields before live use.' })
  return result
}
